import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  Separator,
} from "@devhop/ui";
import { AlertTriangle, Check } from "lucide-react";
import { useState } from "react";
import type { StockAlertWithDetails } from "../../domain/repo/get-stock-alerts";

type StockAlertListProps = {
  alerts: StockAlertWithDetails[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAcknowledge?: (alertId: string) => void;
};

export function StockAlertList({
  alerts,
  open,
  onOpenChange,
  onAcknowledge,
}: StockAlertListProps) {
  const [acknowledging, setAcknowledging] = useState<string | null>(null);

  // Group alerts by warehouse
  const alertsByWarehouse = alerts.reduce(
    (acc, alert) => {
      const warehouseName = alert.warehouse.name;
      if (!acc[warehouseName]) {
        acc[warehouseName] = [];
      }
      acc[warehouseName].push(alert);
      return acc;
    },
    {} as Record<string, StockAlertWithDetails[]>,
  );

  const handleAcknowledge = async (alertId: string) => {
    setAcknowledging(alertId);
    try {
      await onAcknowledge?.(alertId);
    } finally {
      setAcknowledging(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ແຈ້งเຕືອນວັດຖຸດິບໃກ້ຫມດ</DialogTitle>
          <DialogDescription>ລາຍການວັດຖຸດິບທີ່ຕ້ອງສັ່ງຊື້ຫຼືເຕີມສະຕັອກ</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[500px]">
          <div className="space-y-6">
            {Object.entries(alertsByWarehouse).map(
              ([warehouseName, warehouseAlerts]) => (
                <div key={warehouseName} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{warehouseName}</h3>
                    <Badge variant="outline">{warehouseAlerts.length}</Badge>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    {warehouseAlerts.map((alert) => {
                      const isOutOfStock = alert.alertType === "out_of_stock";
                      const currentQty = Number(alert.currentQuantity ?? 0);
                      const thresholdQty = Number(alert.thresholdQuantity ?? 0);

                      return (
                        <div
                          key={alert.id}
                          className={cn(
                            "flex items-start justify-between rounded-lg border p-3",
                            isOutOfStock
                              ? "border-destructive bg-destructive/5"
                              : "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950",
                          )}
                        >
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <AlertTriangle
                                className={cn(
                                  "h-4 w-4",
                                  isOutOfStock
                                    ? "text-destructive"
                                    : "text-orange-500",
                                )}
                              />
                              <span className="font-medium">
                                {alert.ingredient.name}
                              </span>
                              {alert.ingredientVariant.name !==
                                alert.ingredient.name && (
                                <span className="text-muted-foreground text-sm">
                                  ({alert.ingredientVariant.name})
                                </span>
                              )}
                              <Badge
                                variant={
                                  isOutOfStock ? "destructive" : "secondary"
                                }
                                className="text-xs"
                              >
                                {isOutOfStock ? "ຫມົດ" : "ໃກ້ຫມົດ"}
                              </Badge>
                            </div>
                            <div className="text-muted-foreground text-sm">
                              <span>
                                ສະຕັອກປັດຈຸບັນ: {currentQty.toLocaleString("lo-LA")}
                              </span>
                              {thresholdQty > 0 && (
                                <>
                                  <span className="mx-2">•</span>
                                  <span>
                                    ລະດັບຕ່ຳສຸດ:{" "}
                                    {thresholdQty.toLocaleString("lo-LA")}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAcknowledge(alert.id)}
                            disabled={acknowledging === alert.id}
                            className="ml-4"
                          >
                            {acknowledging === alert.id ? (
                              "ກໍາລັງບັນທຶກ..."
                            ) : (
                              <>
                                <Check className="mr-1 h-4 w-4" />
                                ຕົກລົງ
                              </>
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ),
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
